import {useMemo} from 'react'
import {Empty, Space, Table, Typography} from 'antd'
import {DeleteOutlined, EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'
import {formatEmptyValue} from '../../../utils/common'

const ShiftTable = ({
  dataSource,
  pagination = false,
  getShiftListData = null,
  onEdit = null,
  onDelete = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'shifts.plant' }),
        dataIndex: 'plant',
        width: '25%',
        sorter: (a, b) =>
          (a.plant?.name || '').localeCompare(b.plant?.name || ''),
        render: (_, record) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(record.plant?.name)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'shifts.name' }),
        dataIndex: 'name',
        width: '25%',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'shifts.startTime' }),
        dataIndex: 'start_time',
        width: '25%',
        sorter: (a, b) =>
          (a.start_time || '').localeCompare(b.start_time || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'shifts.endTime' }),
        dataIndex: 'end_time',
        width: '25%',
        sorter: (a, b) => (a.end_time || '').localeCompare(b.end_time || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'shifts.action' }),
        align: 'right',
        width: 135,
        render: (_, record) => (
          <TableActions
            actions={[
              {
                key: 'edit',
                label: intl.formatMessage({ id: 'forms.edit' }),
                icon: <EditOutlined />,
                onClick: () => onEdit(record),
              },
              {
                key: 'delete',
                label: intl.formatMessage({ id: 'forms.delete' }),
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onDelete(record),
              },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `shift-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        scroll={{
          y: '70vh',
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getShiftListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default ShiftTable
